import dayjs from "dayjs";
import { get } from "lodash";
import moment from "moment";
import {
  AUTHORITY_TYPE,
  CONTACT_POINT_SYSTEM,
  MAX_FEES_VALUE,
  NIR_INHERITANCE_FROM,
  PAYMENT_RECEIVER,
  REGEX_EXPS,
  SALE_MODES,
} from "utils/constants";
import { isMajor } from "utils/helpers/date";
import {
  childHasSocialRegimeAttachmentMismatch,
  getNirInheritanceFromStored,
  isChildLinkedToAdherent,
  isChildLinkedToSpouse,
  shouldSoftValidateAcorisChildNir,
} from "screens/Private/Broker/Projects/Detail/Templates/Comparison/Acoris/utils/relatedPersonChild";
import {
  attachAcorisForeignCountryTest,
  isAcorisZipCode,
} from "screens/Private/Broker/Projects/Detail/Templates/Comparison/Acoris/utils/zipCode";
import {
  createContractNumberValidation,
  normalizeTextField,
  stripAllSpaces,
} from "utils/helpers/validations";
import {
  getPhonePrefixFromYupContext,
  isValidLocalPhoneNumber,
} from "utils/helpers/phone";
import {
  softValidateNIR,
  validateBIC,
  validateIBAN,
  validateNIR,
} from "utils/helpers/values";
import * as yup from "yup";
import { isPpeEndDateBeforeStart } from "./ppeDeclaration";

/**
 * Save-time validation: format and business-rule checks only when a value is
 * present. Field presence / obligation is driven by the progress registry
 * (index.js), not blocking validation.
 */
const validation = (messages, { hasPartner } = {}) => {
  const string = yup.string().typeError(messages.NOT_STRING).nullable();
  const optionalBoolean = yup.boolean().nullable();
  const textField = yup
    .string()
    .transform(normalizeTextField)
    .typeError(messages.NOT_STRING)
    .nullable();
  const noSpaceString = yup.string().transform(stripAllSpaces).nullable();
  const nameValidation = textField.test(
    "is-valid-name",
    messages.INVALID_NAME,
    (value) => !value || REGEX_EXPS.name.test(value),
  );
  const optionalEmail = yup
    .string()
    .typeError(messages.NOT_STRING)
    .nullable()
    .test(
      "email-format",
      messages.EMAIL,
      (value) => !value || yup.string().email().isValidSync(value),
    );
  const optionalDateInPast = yup
    .date()
    .typeError(messages.NOT_STRING)
    .nullable()
    .test("dueDateFormat", messages.INVALID_DUE_DATE, (value) => {
      if (!value) return true;
      return (
        dayjs(value).isValid() &&
        dayjs(value).isBefore(dayjs().subtract(1, "year"))
      );
    });

  const relatedPersonValidationSchema = ({ birthDate }) =>
    yup.object().shape({
      identity: yup.object().shape({
        user: yup.object().shape({
          lastname: nameValidation.required(messages.REQUIRED),
          firstname: nameValidation.required(messages.REQUIRED),
          birthName: nameValidation,
          birthDate,
        }),
        socioProfessionalCategory: string,
        socialRegime: string.test(
          "spouse-regime-children",
          messages.SOCIAL_REGIME_ATTACHMENT_MISMATCH,
          function (value) {
            const root = this.from[this.from.length - 1].value;
            const children = get(root, "children", []);
            const hasSpouse = Boolean(get(root, "spouse"));
            const hasMismatch = children.some(
              (child) =>
                isChildLinkedToSpouse({
                  authority: get(child, "authority"),
                  nirInheritanceFrom: get(
                    child,
                    "identity.fields.nirInheritanceFrom",
                  ),
                  hasSpouse,
                }) &&
                childHasSocialRegimeAttachmentMismatch({
                  child,
                  adherentSocialRegime: get(
                    root,
                    "project.contact.socialRegime",
                  ),
                  spouseSocialRegime: value,
                  hasSpouse,
                }),
            );
            if (!hasMismatch) return true;
            return this.createError({
              message: messages.SOCIAL_REGIME_ATTACHMENT_MISMATCH,
            });
          },
        ),
        socialSecurityNumber: noSpaceString.test(
          "securityNumberFormat",
          messages.INVALID_VALUE,
          function (value, ctx) {
            if (!value) return false;
            const result = validateNIR(value, this.parent);
            if (!result.valid) {
              return ctx.createError({
                message: messages[result.reason] || messages.INVALID_VALUE,
              });
            }
            return true;
          },
        ),
      }),
    });

  return yup.object().shape({
    createdDate: yup.date().nullable(),
    isPayerReceiver: string,
    additionalInfo: yup
      .object()
      .shape({
        brokerageFees: yup
          .number()
          .max(MAX_FEES_VALUE, `${messages.MAX} ${MAX_FEES_VALUE}`)
          .min(0, `${messages.MIN} 0`)
          .nullable()
          .optional(),
      })
      .optional(),
    project: yup.object().shape({
      projectType: yup.mixed().when("contract.saleMode", {
        is: (saleMode) => saleMode === SALE_MODES.DISTANT,
        then: string,
      }),
      contract: yup.object().shape({
        issuanceDate: yup.date().nullable(),
        caseType: string,
        saleMode: string,
      }),
      fields: yup.object().shape({
        selfOccupation: yup.boolean().nullable(),
        contractNumber: string,
        dueDate: yup.date().nullable(),
        recipient: string,
        reason: string,
        acceptDocumentsReceived: optionalBoolean,
        acceptAgreement: optionalBoolean,
        acceptFees: optionalBoolean,
        acceptProductStatuses: optionalBoolean,
        specificNeeds: string,
        paymentModalities: string,
        commercializationType: string,
      }),
      terminationRequest: yup
        .mixed()
        .when(
          ["fields.selfOccupation", "ria", "fields.contractMoreThanOneYear"],
          {
            is: (selfOccupation, ria, contractMoreThanOneYear) =>
              ria === true &&
              contractMoreThanOneYear === true &&
              selfOccupation === false,
            then: () =>
              yup.object().shape({
                contractNumber: createContractNumberValidation(
                  messages,
                  REGEX_EXPS,
                  { required: false },
                ),
                dueDate: optionalDateInPast,
                brokerageMandate: yup.object().shape({
                  currentCompany: textField,
                }),
                address: string,
                complement: string,
                zipCode: textField.test(
                  "acoris-zip",
                  messages.INVALID_VALUE,
                  (value) => !value || isAcorisZipCode(value),
                ),
                city: string,
                reason: string,
              }),
            otherwise: () => yup.object().nullable().optional(),
          },
        ),
      contact: yup.object().shape({
        user: yup.object().shape({
          gender: string,
          lastname: nameValidation,
          firstname: nameValidation,
          birthName: nameValidation,
          birthDate: yup.date().nullable(),
        }),
        socialRegime: string.test(
          "adherent-regime-children",
          messages.SOCIAL_REGIME_ATTACHMENT_MISMATCH,
          function (value) {
            const root = this.from[this.from.length - 1].value;
            const children = get(root, "children", []);
            const hasSpouse = Boolean(get(root, "spouse"));
            const hasMismatch = children.some(
              (child) =>
                isChildLinkedToAdherent({
                  authority: get(child, "authority"),
                  nirInheritanceFrom: get(
                    child,
                    "identity.fields.nirInheritanceFrom",
                  ),
                }) &&
                childHasSocialRegimeAttachmentMismatch({
                  child,
                  adherentSocialRegime: value,
                  spouseSocialRegime: get(root, "spouse.identity.socialRegime"),
                  hasSpouse,
                }),
            );
            if (!hasMismatch) return true;
            return this.createError({
              message: messages.SOCIAL_REGIME_ATTACHMENT_MISMATCH,
            });
          },
        ),
        socialSecurityNumber: noSpaceString.test(
          "securityNumberFormat",
          messages.INVALID_VALUE,
          function (value, ctx) {
            if (!value) return true;
            const result = validateNIR(value, this.parent);
            if (!result.valid) {
              return ctx.createError({
                message: messages[result.reason] || messages.INVALID_VALUE,
              });
            }
            return true;
          },
        ),
        nationality: string,
        socioProfessionalCategory: string,
        politicallyExposedPerson: optionalBoolean,
        siretNumber: string,
        profession: textField,
        familySituation: textField,
        optMadelin: yup.boolean().nullable(),
        acceptContact: yup.boolean().nullable(),
        acceptDigital: yup.boolean().nullable(),
        fields: yup.object().shape({
          status: textField,
          affiliateOrganizationCode: yup
            .string()
            .typeError(messages.NOT_STRING)
            .nullable(),
          ppeFunction: yup
            .mixed()
            .nullable()
            .test("ppe-function", messages.REQUIRED, function (value) {
              const root = this.from[this.from.length - 1].value;
              if (
                get(root, "project.contact.politicallyExposedPerson") !== true
              ) {
                return true;
              }
              return Boolean(value);
            }),
          ppeStartDate: yup
            .mixed()
            .nullable()
            .test("ppe-start", messages.REQUIRED, function (value) {
              const root = this.from[this.from.length - 1].value;
              if (
                get(root, "project.contact.politicallyExposedPerson") !== true
              ) {
                return true;
              }
              return Boolean(value);
            }),
          ppeEndDate: yup
            .mixed()
            .nullable()
            .test(
              "ppe-end-after-start",
              messages.ACORIS?.PPE_END_BEFORE_START,
              function (value) {
                return !isPpeEndDateBeforeStart(
                  this.parent.ppeStartDate,
                  value,
                );
              },
            ),
        }),
      }),
    }),
    telecoms: yup.object().shape({
      contact: attachAcorisForeignCountryTest(
        yup.object().shape({
          [CONTACT_POINT_SYSTEM.ADDRESS]: yup.object().shape({
            value: textField,
          }),
          [CONTACT_POINT_SYSTEM.ADDRESS_LOCATION]: yup.object().shape({
            value: textField,
          }),
          [CONTACT_POINT_SYSTEM.COMPLEMENT]: yup.object().shape({
            value: textField,
          }),
          [CONTACT_POINT_SYSTEM.RESIDENCE]: yup.object().shape({
            value: textField,
          }),
          [CONTACT_POINT_SYSTEM.ZIP_CODE]: yup.object().shape({
            value: textField.test(
              "acoris-zip",
              messages.INVALID_VALUE,
              (value) => !value || isAcorisZipCode(value),
            ),
          }),
          [CONTACT_POINT_SYSTEM.CITY]: yup.object().shape({
            value: string,
          }),
          [CONTACT_POINT_SYSTEM.COUNTRY]: yup.object().shape({
            value: textField,
          }),
          [CONTACT_POINT_SYSTEM.BIRTH_COUNTRY]: yup.object().shape({
            value: textField,
          }),
          [CONTACT_POINT_SYSTEM.BIRTH_CITY]: yup.object().shape({
            value: textField,
          }),
          [CONTACT_POINT_SYSTEM.PHONE]: yup.object().shape({
            value: noSpaceString
              .required(messages.REQUIRED)
              .test("isValid", messages.INVALID_VALUE, function (value) {
                if (!value) return true;
                const prefix = getPhonePrefixFromYupContext(this);
                return isValidLocalPhoneNumber(value, prefix);
              }),
          }),
          [CONTACT_POINT_SYSTEM.LANDLINE]: yup.object().shape({
            value: noSpaceString.test(
              "isValid",
              messages.INVALID_VALUE,
              function (value) {
                return value ? new RegExp(REGEX_EXPS.phone).test(value) : true;
              },
            ),
          }),
          [CONTACT_POINT_SYSTEM.EMAIL]: yup.object().shape({
            value: optionalEmail,
          }),
        }),
        messages.REQUIRED,
      ),
      payer: attachAcorisForeignCountryTest(
        yup.object().shape({
          [CONTACT_POINT_SYSTEM.ADDRESS]: yup.object().shape({
            value: textField,
          }),
          [CONTACT_POINT_SYSTEM.ZIP_CODE]: yup.object().shape({
            value: string.test(
              "acoris-zip",
              messages.INVALID_VALUE,
              (value) => !value || isAcorisZipCode(value),
            ),
          }),
          [CONTACT_POINT_SYSTEM.CITY]: yup.object().shape({
            value: string,
          }),
          [CONTACT_POINT_SYSTEM.COUNTRY]: yup.object().shape({
            value: string,
          }),
        }),
        messages.REQUIRED,
      ),
      receiver: yup.object().shape({}),
    }),
    spouse:
      hasPartner &&
      relatedPersonValidationSchema({
        birthDate: yup
          .date()
          .nullable()
          .test("spouse-age", messages.SPOUSE_AGE, (value) => {
            if (!value) return true;
            return (
              moment(value).isSameOrBefore(isMajor) &&
              moment(value).isSameOrAfter(
                moment().subtract(100, "year").startOf("year"),
              )
            );
          })
          .required(messages.REQUIRED),
      }),
    children: yup.array().of(
      yup
        .object()
        .shape({
          authority: yup
            .string()
            .nullable()
            .test("both-spouse", messages.INVALID_VALUE, function (value) {
              if (value !== AUTHORITY_TYPE.BOTH) return true;
              if (
                get(this.parent, "identity.fields.nirInheritanceFrom") !==
                NIR_INHERITANCE_FROM.SPOUSE
              ) {
                return true;
              }
              return true;
            })
            .test(
              "authority-regime-match",
              messages.SOCIAL_REGIME_ATTACHMENT_MISMATCH,
              function (value) {
                const child = this.parent;
                const root = this.from[this.from.length - 1].value;
                const hasSpouse = Boolean(get(root, "spouse"));
                if (
                  !childHasSocialRegimeAttachmentMismatch({
                    child: { ...child, authority: value },
                    adherentSocialRegime: get(
                      root,
                      "project.contact.socialRegime",
                    ),
                    spouseSocialRegime: get(
                      root,
                      "spouse.identity.socialRegime",
                    ),
                    hasSpouse,
                  })
                ) {
                  return true;
                }
                return this.createError({
                  message: messages.SOCIAL_REGIME_ATTACHMENT_MISMATCH,
                });
              },
            ),
          identity: yup.object().shape({
            user: yup.object().shape({
              lastname: nameValidation.required(messages.REQUIRED),
              firstname: nameValidation.required(messages.REQUIRED),
              birthDate: yup
                .date()
                .required(messages.REQUIRED)
                .test("child-birth-date", messages.MIN_TODAY, (value) => {
                  if (!value) return true;
                  return moment(value).isSameOrBefore(moment());
                }),
            }),
            socioProfessionalCategory: string,
            socialRegime: string,
            socialSecurityNumber: yup.string().nullable(),
            fields: yup.object().nullable().notRequired(),
          }),
        })
        .test(
          "child-social-regime-attachment",
          messages.SOCIAL_REGIME_ATTACHMENT_MISMATCH,
          function (child) {
            const root = this.from[this.from.length - 1].value;
            const hasSpouse = Boolean(get(root, "spouse"));
            if (
              !childHasSocialRegimeAttachmentMismatch({
                child,
                adherentSocialRegime: get(root, "project.contact.socialRegime"),
                spouseSocialRegime: get(root, "spouse.identity.socialRegime"),
                hasSpouse,
              })
            ) {
              return true;
            }
            return this.createError({
              path: `${this.path}.identity.socialRegime`,
              message: messages.SOCIAL_REGIME_ATTACHMENT_MISMATCH,
            });
          },
        )
        .test("child-nir-format", messages.INVALID_VALUE, function (child) {
          const value = get(child, "identity.socialSecurityNumber");
          if (!value) return true;
          const root = this.from[this.from.length - 1].value;
          const result = shouldSoftValidateAcorisChildNir({
            authority: get(child, "authority"),
            childNir: value,
            adherentNir: get(root, "project.contact.socialSecurityNumber"),
            spouseNir: get(root, "spouse.identity.socialSecurityNumber"),
            nirInheritanceFrom: getNirInheritanceFromStored(child),
          })
            ? softValidateNIR(value)
            : validateNIR(value, child.identity);
          if (!result.valid) {
            return this.createError({
              path: `${this.path}.identity.socialSecurityNumber`,
              message: messages[result.reason] || messages.INVALID_VALUE,
            });
          }
          return true;
        }),
    ),
    payment: yup.object().shape({
      type: string,
      frequency: string,
      debitDay: string,
      payer: yup.object().shape({
        BIC: noSpaceString.test(
          "validateBIC",
          messages.INVALID_BIC || messages.INVALID_IBAN,
          function (value) {
            if (!value) return true;
            return validateBIC(value).valid;
          },
        ),
        IBAN: noSpaceString.test(
          "validateIBAN",
          messages.INVALID_IBAN,
          function (value) {
            if (!value) return true;
            return validateIBAN(value).valid;
          },
        ),
        owner: yup.object().shape({
          isAdherent: yup.boolean().nullable(),
          firstName: nameValidation,
          lastName: nameValidation,
        }),
      }),
      receiver: yup.object().shape({
        BIC: noSpaceString.test(
          "validateBIC",
          messages.INVALID_BIC || messages.INVALID_IBAN,
          function (value) {
            const root = this.from[this.from.length - 1].value;
            if (
              get(root, "isPayerReceiver") !== PAYMENT_RECEIVER.ANOTHER_ACCOUNT
            ) {
              return true;
            }
            if (!value) return true;
            return validateBIC(value).valid;
          },
        ),
        IBAN: noSpaceString.test(
          "validateIBAN",
          messages.INVALID_IBAN,
          function (value) {
            const root = this.from[this.from.length - 1].value;
            if (
              get(root, "isPayerReceiver") !== PAYMENT_RECEIVER.ANOTHER_ACCOUNT
            ) {
              return true;
            }
            if (!value) return true;
            return validateIBAN(value).valid;
          },
        ),
        owner: yup.object().shape({
          firstName: nameValidation,
          lastName: nameValidation,
        }),
      }),
    }),
  });
};

export default validation;
